import { SquarePost } from '@/app/actions/share';
import LikeButton from '@/components/like-button';
import ShareCardLink from './share-card-link';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { BookOpen, Eye } from 'lucide-react';
import dayjs from 'dayjs';
import Image from 'next/image';

export default function ShareCard({
  item,
  className,
  priority = false,
}: {
  item: SquarePost;
  className?: string;
  priority?: boolean;
}) {
  const url = `/share/p/${item.token}`;
  const authorName = item.anonymous ? '匿名用户' : item.author_display_name;
  const sourceLabel = item.source_type === 'DEEP_RESEARCH' ? '深度研究' : '文献检索';
  const sourceBadgeClassName =
    item.source_type === 'DEEP_RESEARCH'
      ? 'border-sky-200/80 bg-sky-50 text-sky-700 dark:border-sky-900/70 dark:bg-sky-950/40 dark:text-sky-300'
      : 'border-emerald-200/80 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300';
  return (
    <div
      className={cn(
        'group relative flex flex-col rounded-xl overflow-hidden',
        'bg-card border border-border/50',
        'hover:border-border transition-colors hover:shadow-xs cursor-pointer',
        className,
      )}
    >
      <ShareCardLink href={url} title={item.title} />
      <div className="relative w-full overflow-hidden rounded-t-xl">
        {item.cover_url ? (
          <Image
            width={394}
            height={206}
            src={item.cover_url}
            alt={item.title}
            className="max-w-full w-full transition-transform duration-300 group-hover:scale-[1.02]"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            priority={priority}
            draggable={false}
          />
        ) : (
          <div className="w-full h-full bg-muted/50 pt-[56.25%] relative">
            <div className="absolute inset-0 px-4 space-y-3 flex flex-col items-center justify-center">
              <div className="text-xs text-muted-foreground">
                正在生成封面...
              </div>
              <Skeleton className="w-[60%] h-4 rounded-full" />
              <Skeleton className="w-[80%] h-4 rounded-full" />
              <Skeleton className="w-[70%] h-4 rounded-full" />
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 p-3">
        <div
          className="font-semibold text-sm truncate mb-1.5 block transition-colors"
          title={item.title}
        >
          {item.title}
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2 min-h-10 mb-3">
          {item.summary || '公开广场精选内容，可查看摘要与互动数据。'}
        </p>
        <div className="mb-2 flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <BookOpen className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate max-w-[96px] text-xs font-medium text-muted-foreground">
              {authorName}
            </span>
          </div>
          <span
            className={cn(
              'rounded-full border px-2 py-0.5 text-[11px] font-medium',
              sourceBadgeClassName,
            )}
          >
            {sourceLabel}
          </span>
        </div>
        <div className="flex flex-row items-center justify-between mt-auto pt-2 border-t border-border/50">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-muted-foreground/60">
              {dayjs(item.published_at).format('YYYY/MM/DD')}
            </span>
            <span className="text-xs text-muted-foreground/60">
              ·
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/60">
              <Eye className="w-3.5 h-3.5" />
              {item.view_count}
            </span>
          </div>
          <div className="relative z-20">
            <LikeButton
              token={item.token}
              count={item.like_count}
              isLiked={!!item.current_user_liked}
              postId={item.post_id}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function ShareCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-col rounded-xl overflow-hidden',
        'bg-card border border-border/50',
        className,
      )}
    >
      <Skeleton className="aspect-video w-full rounded-none rounded-t-xl" />
      <div className="flex flex-col flex-1 p-3">
        <Skeleton className="h-5 w-[82%] rounded-full" />
        <div className="mt-2 space-y-2">
          <Skeleton className="h-4 w-full rounded-full" />
          <Skeleton className="h-4 w-[76%] rounded-full" />
        </div>
        <div className="mt-3 flex items-center gap-2 min-w-0">
          <Skeleton className="h-4 w-20 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-border/50">
          <Skeleton className="h-4 w-26 rounded-full" />
          <Skeleton className="h-8 w-14 rounded-full" />
        </div>
      </div>
    </div>
  );
}
